import { useCallback, useRef, useState } from 'react'

import type { LotoSession } from './useLotoSession'

export type LotoLeavePrompt = {
  open: boolean
  reportName: string | undefined
  saving: boolean
  saveError: Error | undefined
  requestLeave: (leave: () => void) => void
  isDirty: () => boolean
  saveAndContinue: () => Promise<void>
  discardAndContinue: () => void
  cancel: () => void
}

type LotoLeaveSource = Pick<
  LotoSession,
  'report' | 'hasChanges' | 'saving' | 'saveError' | 'save' | 'discard'
>

export function useLotoLeavePrompt(
  session: LotoLeaveSource | undefined
): LotoLeavePrompt {
  const [open, setOpen] = useState(false)
  const leaveRef = useRef<() => void>()
  const leavingRef = useRef(false)
  const hasChanges = session?.hasChanges ?? false
  const save = session?.save
  const discard = session?.discard

  const requestLeave = useCallback(
    (leave: () => void): void => {
      if (!hasChanges || leavingRef.current) return leave()
      leaveRef.current = leave
      setOpen(true)
    },
    [hasChanges]
  )
  const proceed = useCallback((): void => {
    const leave = leaveRef.current
    leaveRef.current = undefined
    setOpen(false)
    leavingRef.current = true
    try {
      leave?.()
    } finally {
      leavingRef.current = false
    }
  }, [])
  const isDirty = useCallback(
    () => hasChanges && !leavingRef.current,
    [hasChanges]
  )
  const saveAndContinue = useCallback(async (): Promise<void> => {
    if (save && (await save())) proceed()
  }, [proceed, save])
  const discardAndContinue = useCallback((): void => {
    discard?.()
    proceed()
  }, [discard, proceed])
  const cancel = useCallback((): void => {
    leaveRef.current = undefined
    setOpen(false)
  }, [])

  return {
    open,
    reportName: session?.report?.data.name,
    saving: session?.saving ?? false,
    saveError: session?.saveError,
    requestLeave,
    isDirty,
    saveAndContinue,
    discardAndContinue,
    cancel,
  }
}
